import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { preventasApi, cajaApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSucursal } from '../context/SucursalContext';
import { formatDate } from '../utils/dateHelpers';
import PagoMixtoForm, { lineaVacia, validarPagoMixto } from '../components/PagoMixtoForm';

const Preventas = () => {
  const { user } = useAuth();
  const { sucursalActual, getSucursalId } = useSucursal();

  // Estado del listado
  const [preventas, setPreventas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [stats, setStats] = useState(null);

  // Estado del formulario
  const [showForm, setShowForm] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    cliente_nombre: '',
    cliente_telefono: '',
    direccion: '',
    nit: 'C/F',
    items: [{ cantidad: 1, descripcion: '', precio_unitario: 0, total: 0 }],
    anticipo: 0,
    metodo_pago_anticipo: 'Efectivo',
    cuenta_bancaria_id: '',
    pos_id: '',
    comprobante: '',
    anticipo_mixto: [lineaVacia()]
  });

  const [cuentas, setCuentas] = useState([]);
  const [posTerminales, setPosTerminales] = useState([]);

  useEffect(() => {
    const tiendaId = getSucursalId();
    if (!tiendaId) return;
    Promise.all([cajaApi.getCuentas(tiendaId), cajaApi.getPos(tiendaId)])
      .then(([resCuentas, resPos]) => {
        setCuentas(resCuentas || []);
        setPosTerminales(resPos || []);
      })
      .catch(err => console.error('Error cargando cuentas/POS:', err));
  }, [getSucursalId]);

  const loadPreventas = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      const tiendaId = getSucursalId();
      if (tiendaId) params.tienda_id = tiendaId;
      if (filtroEstado) params.estado = filtroEstado;

      const data = await preventasApi.getAll(params);
      setPreventas(data.data || []);
      setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      console.error('Error al cargar preventas:', err);
    } finally {
      setLoading(false);
    }
  }, [getSucursalId, filtroEstado]);

  const loadStats = useCallback(async () => {
    try {
      const params = {};
      const tiendaId = getSucursalId();
      if (tiendaId) params.tienda_id = tiendaId;
      const data = await preventasApi.getStats(params);
      setStats(data);
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
    }
  }, [getSucursalId]);

  useEffect(() => {
    loadPreventas();
    loadStats();
  }, [loadPreventas, loadStats]);

  const handleBuscar = async () => {
    if (!busqueda.trim()) {
      loadPreventas();
      return;
    }
    setLoading(true);
    try {
      const data = await preventasApi.search(busqueda);
      setPreventas(data || []);
      setPagination({ page: 1, pages: 1, total: (data || []).length });
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const calcularTotal = (items) => items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);

  const handleItemChange = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index][field] = value;
    if (field === 'cantidad' || field === 'precio_unitario') {
      newItems[index].total = (parseFloat(newItems[index].cantidad) || 0) * (parseFloat(newItems[index].precio_unitario) || 0);
    }
    setForm(prev => ({ ...prev, items: newItems }));
  };

  const agregarItem = () => {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { cantidad: 1, descripcion: '', precio_unitario: 0, total: 0 }]
    }));
  };

  const eliminarItem = (index) => {
    if (form.items.length > 1) {
      setForm(prev => ({ ...prev, items: form.items.filter((_, i) => i !== index) }));
    }
  };

  const formatMoney = (amount) => `Q${parseFloat(amount || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;


  const totalForm = calcularTotal(form.items);
  const anticipoForm = Math.min(parseFloat(form.anticipo) || 0, totalForm);
  const saldoForm = totalForm - anticipoForm;

  const handleGuardar = async () => {
    if (!form.cliente_nombre.trim()) {
      alert('Debe ingresar el nombre del cliente');
      return;
    }
    const itemsValidos = form.items.filter(item => item.descripcion.trim() && parseFloat(item.total) > 0);
    if (itemsValidos.length === 0) {
      alert('Debe agregar al menos un item con descripcion y precio');
      return;
    }
    if (anticipoForm > 0) {
      if (form.metodo_pago_anticipo === 'Transferencia' && !form.cuenta_bancaria_id) {
        alert('Debe seleccionar la cuenta bancaria');
        return;
      }
      if (form.metodo_pago_anticipo === 'POS' && !form.pos_id) {
        alert('Debe seleccionar la terminal POS');
        return;
      }
      if (form.metodo_pago_anticipo === 'Mixto') {
        const errorMixto = validarPagoMixto(form.anticipo_mixto, anticipoForm);
        if (errorMixto) {
          alert(errorMixto);
          return;
        }
      }
    }

    setGuardando(true);
    try {
      const tiendaId = getSucursalId();
      const result = await preventasApi.create({
        fecha: form.fecha,
        cliente_nombre: form.cliente_nombre,
        cliente_telefono: form.cliente_telefono,
        direccion: form.direccion,
        nit: form.nit,
        items: itemsValidos,
        anticipo: anticipoForm,
        metodo_pago_anticipo: form.metodo_pago_anticipo,
        cuenta_bancaria_id: form.cuenta_bancaria_id || null,
        pos_id: form.pos_id || null,
        anticipo_mixto: form.metodo_pago_anticipo === 'Mixto' ? form.anticipo_mixto : undefined,
        creado_por: user?.id,
        tienda_id: tiendaId || null
      });

      limpiarFormulario();
      setShowForm(false);
      loadPreventas();
      loadStats();
      alert(`Preventa N° ${result.numero_preventa} guardada exitosamente`);
    } catch (err) {
      console.error('Error:', err);
      alert('Error al guardar la preventa: ' + (err.response?.data?.error || err.message));
    } finally {
      setGuardando(false);
    }
  };

  const limpiarFormulario = () => {
    setForm({
      fecha: new Date().toISOString().split('T')[0],
      cliente_nombre: '',
      cliente_telefono: '',
      direccion: '',
      nit: 'C/F',
      items: [{ cantidad: 1, descripcion: '', precio_unitario: 0, total: 0 }],
      anticipo: 0,
      metodo_pago_anticipo: 'Efectivo',
      cuenta_bancaria_id: '',
      pos_id: '',
      comprobante: '',
      anticipo_mixto: [lineaVacia()]
    });
  };

  const handleEliminar = async (id, numero) => {
    if (!window.confirm(`¿Eliminar la preventa N° ${numero}?`)) return;
    try {
      await preventasApi.delete(id);
      loadPreventas();
      loadStats();
    } catch (err) {
      alert('Error al eliminar: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Preventas</h1>
          <p className="page-subtitle">Ventas con anticipo y saldo pendiente</p>
        </div>
        <button onClick={() => { setShowForm(true); limpiarFormulario(); }} className="btn btn-primary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="16"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
          Nueva Preventa
        </button>
      </div>

      {/* Estadísticas */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-info">
              <h3>{stats.total_preventas}</h3>
              <p>Total Preventas ({formatMoney(stats.total_monto)})</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-info">
              <h3 style={{ color: 'var(--danger-color)' }}>{stats.cantidad_pendientes}</h3>
              <p>Pendientes de Cobro ({formatMoney(stats.total_pendiente)})</p>
            </div>
          </div>
        </div>
      )}

      {/* Busqueda y filtro */}
      <div className="card">
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <input
            type="text"
            className="form-control"
            placeholder="Buscar por numero o cliente..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleBuscar()}
            style={{ flex: 1, minWidth: '200px' }}
          />
          <select
            className="form-control"
            style={{ width: 'auto', minWidth: '160px' }}
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
          >
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendientes</option>
            <option value="pagada">Pagadas</option>
          </select>
          <button onClick={handleBuscar} className="btn btn-primary">Buscar</button>
          {busqueda && (
            <button onClick={() => { setBusqueda(''); loadPreventas(); }} className="btn btn-secondary">Limpiar</button>
          )}
        </div>
      </div>

      {/* Listado */}
      <div className="card">
        {loading ? (
          <div className="loading"><div className="spinner"></div></div>
        ) : preventas.length === 0 ? (
          <div className="empty-state">
            <h3>No hay preventas</h3>
            <p>Crea una nueva preventa para empezar</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>N° Preventa</th>
                    <th>Fecha Preventa</th>
                    <th>Fecha Abono</th>
                    <th>Fecha Cancelación</th>
                    <th>Cliente</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'right' }}>Anticipo</th>
                    <th style={{ textAlign: 'right' }}>Saldo</th>
                    <th>Estado</th>
                    <th style={{ textAlign: 'center' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {preventas.map(p => (
                    <tr key={p.id}>
                      <td>
                        <Link to={`/preventas/${p.id}`} style={{ fontWeight: 600, color: 'var(--primary-color)' }}>
                          {p.numero_preventa}
                        </Link>
                      </td>
                      <td>{formatDate(p.fecha)}</td>
                      <td>{parseFloat(p.anticipo) > 0 ? formatDate(p.created_at) : '-'}</td>
                      <td>{p.estado === 'pagada' ? formatDate(p.fecha_pago_final) : '-'}</td>
                      <td style={{ fontWeight: 500 }}>{p.cliente_nombre}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoney(p.total)}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoney(p.anticipo)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: parseFloat(p.saldo) > 0 ? 'var(--danger-color)' : 'var(--success-color)' }}>
                        {formatMoney(p.saldo)}
                      </td>
                      <td>
                        <span className={`status-badge ${p.estado === 'pagada' ? 'status-terminada' : 'status-pendiente'}`}>
                          {p.estado === 'pagada' ? 'Pagada' : 'Pendiente'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <Link to={`/preventas/${p.id}`} className="btn btn-secondary btn-sm">Ver</Link>
                          <button onClick={() => handleEliminar(p.id, p.numero_preventa)} className="btn btn-danger btn-sm">
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => loadPreventas(page)}
                    className={`btn ${page === pagination.page ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de nueva preventa */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2 className="modal-title">Nueva Preventa</h2>
              <button className="modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Fecha</label>
                  <input type="date" className="form-control" value={form.fecha}
                    onChange={(e) => setForm(prev => ({ ...prev, fecha: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">NIT</label>
                  <input type="text" className="form-control" value={form.nit} placeholder="C/F"
                    onChange={(e) => setForm(prev => ({ ...prev, nit: e.target.value }))} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className="form-group">
                  <label className="form-label required">Cliente</label>
                  <input type="text" className="form-control" placeholder="Nombre del cliente" value={form.cliente_nombre}
                    onChange={(e) => setForm(prev => ({ ...prev, cliente_nombre: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input type="text" className="form-control" value={form.cliente_telefono}
                    onChange={(e) => setForm(prev => ({ ...prev, cliente_telefono: e.target.value }))} />
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">Dirección</label>
                <input type="text" className="form-control" value={form.direccion}
                  onChange={(e) => setForm(prev => ({ ...prev, direccion: e.target.value }))} />
              </div>

              {/* Items */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>Artículos / Servicios</h3>
                  <button type="button" onClick={agregarItem} className="btn btn-secondary btn-sm">+ Línea</button>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-color)', textAlign: 'left' }}>
                      <th style={{ padding: '6px', width: '70px', fontSize: '12px' }}>Cant.</th>
                      <th style={{ padding: '6px', fontSize: '12px' }}>Descripción</th>
                      <th style={{ padding: '6px', width: '100px', fontSize: '12px' }}>P. Unit.</th>
                      <th style={{ padding: '6px', width: '100px', fontSize: '12px' }}>Total</th>
                      <th style={{ padding: '6px', width: '30px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((item, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '4px' }}>
                          <input type="number" min="1" className="form-control" style={{ padding: '6px', fontSize: '13px' }}
                            value={item.cantidad} onChange={(e) => handleItemChange(index, 'cantidad', e.target.value)} />
                        </td>
                        <td style={{ padding: '4px' }}>
                          <input type="text" className="form-control" style={{ padding: '6px', fontSize: '13px' }}
                            placeholder="Descripción del producto o servicio"
                            value={item.descripcion} onChange={(e) => handleItemChange(index, 'descripcion', e.target.value)} />
                        </td>
                        <td style={{ padding: '4px' }}>
                          <input type="number" step="0.01" min="0" className="form-control"
                            style={{ padding: '6px', fontSize: '13px', textAlign: 'right' }}
                            value={item.precio_unitario} onChange={(e) => handleItemChange(index, 'precio_unitario', e.target.value)} />
                        </td>
                        <td style={{ padding: '4px', textAlign: 'right', fontWeight: 600, fontSize: '13px' }}>
                          {formatMoney(item.total)}
                        </td>
                        <td style={{ padding: '4px', textAlign: 'center' }}>
                          {form.items.length > 1 && (
                            <button type="button" onClick={() => eliminarItem(index)}
                              style={{ background: 'none', border: 'none', color: 'var(--danger-color)', cursor: 'pointer', fontSize: '16px' }}>
                              &times;
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Anticipo */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Anticipo (Q)</label>
                  <input type="number" step="0.01" min="0" max={totalForm} className="form-control" value={form.anticipo}
                    onChange={(e) => setForm(prev => ({ ...prev, anticipo: e.target.value }))} />
                </div>
                {anticipoForm > 0 && (
                  <div className="form-group">
                    <label className="form-label">Método del Anticipo</label>
                    <select className="form-control" value={form.metodo_pago_anticipo}
                      onChange={(e) => setForm(prev => ({ ...prev, metodo_pago_anticipo: e.target.value, cuenta_bancaria_id: '', pos_id: '' }))}>
                      <option value="Efectivo">Efectivo</option>
                      <option value="Transferencia">Transferencia</option>
                      <option value="POS">POS</option>
                      <option value="Cheque">Cheque</option>
                      <option value="Mixto">Pago Mixto</option>
                    </select>
                  </div>
                )}
                {anticipoForm > 0 && form.metodo_pago_anticipo === 'Transferencia' && (
                  <div className="form-group">
                    <label className="form-label required">Cuenta Bancaria</label>
                    <select className="form-control" value={form.cuenta_bancaria_id}
                      onChange={(e) => setForm(prev => ({ ...prev, cuenta_bancaria_id: e.target.value }))}>
                      <option value="">-- Seleccionar --</option>
                      {cuentas.map(c => <option key={c.id} value={c.id}>{c.nombre} - {c.banco}</option>)}
                    </select>
                  </div>
                )}
                {anticipoForm > 0 && form.metodo_pago_anticipo === 'POS' && (
                  <div className="form-group">
                    <label className="form-label required">Terminal POS</label>
                    <select className="form-control" value={form.pos_id}
                      onChange={(e) => setForm(prev => ({ ...prev, pos_id: e.target.value }))}>
                      <option value="">-- Seleccionar --</option>
                      {posTerminales.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {anticipoForm > 0 && form.metodo_pago_anticipo === 'Mixto' && (
                <div style={{ marginBottom: '16px' }}>
                  <label className="form-label">Desglose del Anticipo</label>
                  <PagoMixtoForm
                    pagos={form.anticipo_mixto}
                    onChange={(pagos) => setForm(prev => ({ ...prev, anticipo_mixto: pagos }))}
                    total={anticipoForm}
                    cuentas={cuentas}
                    posTerminales={posTerminales}
                  />
                </div>
              )}

              {/* Totales */}
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span>Total</span>
                  <span style={{ fontWeight: 600 }}>{formatMoney(totalForm)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span>Anticipo</span>
                  <span style={{ fontWeight: 600, color: 'var(--success-color)' }}>{formatMoney(anticipoForm)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>
                  <span style={{ fontWeight: 600 }}>Saldo Pendiente</span>
                  <span style={{ fontSize: '20px', fontWeight: 700, color: saldoForm > 0 ? 'var(--danger-color)' : 'var(--success-color)' }}>
                    {formatMoney(saldoForm)}
                  </span>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={handleGuardar} disabled={guardando}>
                {guardando ? 'Guardando...' : 'Guardar Preventa'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Preventas;
